import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { FacebookProvider, Login } from "react-facebook";
import { Button } from "reactstrap";
import {
  INSTAGRAM_MENSSEGER_GET_LOADING,
  INSTAGRAM_MENSSEGER_SYNC_LOADING,
} from "../../../../constants/loading.constant";
import { INSTAGRAM_MENSSEGER_BIND_MODAL } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useInstagramMenssegerCore } from "../../../../cores/instagramMensseger";
import { APP_ID, SCOPE } from "../../../../cores/instagramMensseger/config";
import { useMeCore } from "../../../../cores/me";
import {
  SocialProviderId,
  SocialProviderName,
} from "../../../../cores/socialPost/config";
import { useIntl } from "react-intl";
import { SocialProvider } from "../../../../cores/socialPost/interfaces/socialProvider";
import { daysRemainingFromToday } from "../../../utils/dateUtils";
import SocialCard from "../SocialCard";
import { InstagramMenssegerIcon } from "./InstagramMenssegerIcon";
import InstagramMenssegerLoginForm from "./InstagramMenssegerLoginForm";

interface InstagramProviderResponse {
  tokenDetail: { accessToken: string };
}

export default function InstagramMenssegerBindCard() {
  const intl = useIntl();
  const { get, settings, unbind, getAccounts } = useInstagramMenssegerCore();
  const { company, getMeSocialSettings } = useMeCore();
  const { turnOnModal, isLoading } = useGlobalCore();



  useEffect(() => {
    get(company!.id);
  }, [company, get]);

  const socialProvider = useMemo(
    (): SocialProvider => ({
      id: SocialProviderId.InstagramMensseger,
      name: SocialProviderName.InstagramMensseger,
      icon: <InstagramMenssegerIcon size="5x" active={!_.isEmpty(settings)} />,
      displayName: settings?.name,
      avatar: settings?.avatarUrl,
    }),
    [settings]
  );

  const handleOnError = useCallback(
    (error: string) => console.error(error),
    []
  );

  const remainingBindDays = useMemo(
    () =>
      settings?.expiresIn ? daysRemainingFromToday(settings?.expiresIn) : null,
    [settings]
  );

  const handleOnCompleted = useCallback(
    async ({ tokenDetail: { accessToken } }: InstagramProviderResponse) => {
      await getAccounts(company!.id, accessToken);
      turnOnModal(
        INSTAGRAM_MENSSEGER_BIND_MODAL,
        <InstagramMenssegerLoginForm userToken={accessToken} />
      );
    },
    [company, getAccounts, turnOnModal]
  );

  const handleUnbind = useCallback(() => {
    unbind(company!.id, () => {
      getMeSocialSettings(company?.id!);
    });
  }, [company, unbind, getMeSocialSettings]);

  const button = useMemo(() => {
    return _.isEmpty(settings) ? (
      <FacebookProvider version="v10.0" appId={APP_ID}>
        <Login
          scope={SCOPE}
          onCompleted={handleOnCompleted}
          onError={handleOnError}
        >
          {({ handleClick }: { handleClick: () => void }) => (
            <Button
              className="w-100"
              color="primary"
              onClick={handleClick}
            >
              {intl.formatMessage({ id: "app.home.syncUp" })}
            </Button>
          )}
        </Login>
      </FacebookProvider>
    ) : (
      <Button
        className="w-100"
        color="primary"
        outline
        onClick={handleUnbind}
      >
        {intl.formatMessage({ id: "app.home.disconnect" })}
      </Button>
    );
  }, [handleOnCompleted, handleOnError, handleUnbind, settings, intl]);

  return (
    <SocialCard
      social={socialProvider}
      bindLoading={isLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING)}
      getLoading={isLoading(INSTAGRAM_MENSSEGER_GET_LOADING)}
      videoId="Vi_sDfGGt1A"
      title={intl.formatMessage({id: "app.videosHome.InstagramMesseneger"})}
    >
      <div className="w-100 px-2">
        {remainingBindDays !== null && remainingBindDays > 0 && remainingBindDays <= 10 && (
          <div className="small text-danger mb-1 d-flex">
            <FontAwesomeIcon
              size={"2x"}
              icon={["fas", "exclamation-circle"]}
              className="mr-2"
            />
            {intl.formatMessage(
              { id: "app.home.expiration" },
              {
                remainingDays: remainingBindDays,
              }
            )}
          </div>
        )}

        {remainingBindDays !== null && remainingBindDays <= 0 &&
          <div className="small text-danger mb-1 d-flex  px-2"> 
            <FontAwesomeIcon
              size={"2x"}
              icon={["fas", "exclamation-circle"]}
              className="mr-2"
            />
              {intl.formatMessage({id: "app.home.expired"})}
          </div>  
        }
        {button}
      </div>
    </SocialCard>
  );
}
